import { ReactNode, useEffect, useState } from 'react';
import { Configuration, OrdersApi } from '@/api';
import useGetOrderQuery from '@/api/queries/useGetOrderQuery';
import Address from '@/components/Address';
import CustomToast from '@/components/CustomToast';
import DeliveryWindowSelectBox, { DeliveryWindowItem } from '@/components/DeliveryWindowSelectBox';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { DeliveryWindow, DeliveryWindowImpl } from '@/models/DeliveryWindow';
import {
  OrderIncompleteDeliveryAlreadyReportedError,
  OrderIncompleteDeliveryReason,
} from '@/models/OrderIncompleteDelivery';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import OrderItemEditTable from '@/modules/order/components/OrderItemEditTable';
import OrderItemTable from '@/modules/order/components/OrderItemTable';
import type { Order } from '@/modules/order/models/Order';
import { OrderStatus as OrderStatusModel } from '@/modules/order/models/OrderStatus';
import { calculateConfirmedOrderItemPrice } from '@/modules/order/services/calculateConfirmedOrderItemPrice';
import { formatTimeWindow } from '@/utils/date/formatTimeWindow';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

interface OrderItemQuantities {
  [orderItemId: string]: number;
}

export interface PrintOrderOptions {
  hidePriceData?: boolean;
}

const areConfirmedQuantitiesEqual = (
  currentQuantities: OrderItemQuantities | undefined,
  nextQuantities: OrderItemQuantities,
): boolean => {
  if (currentQuantities === undefined) {
    return false;
  }

  const currentIds = Object.keys(currentQuantities);
  const nextIds = Object.keys(nextQuantities);

  return (
    currentIds.length === nextIds.length &&
    nextIds.every((id) => currentQuantities[id] === nextQuantities[id])
  );
};

interface State {
  isEditing: boolean;
  hidePriceDataInPrint: boolean;
  confirmedQuantities?: OrderItemQuantities;
  confirmedQuantitiesOrderId?: string;
  deliveryWindow?: DeliveryWindow;
  reportedIncompleteDeliveryOrderId?: string;
}

export interface OrderDetailsNavProps {
  order: Order;
  isEditing: boolean;
  onPrintOrder: (options?: PrintOrderOptions) => void;
  onDiscardChangesClick: () => void;
  onUpdateAndAcceptOrder: () => Promise<void>;
  onDispatchOrder: () => Promise<void>;
  onCompleteOrder: () => Promise<void>;
  onRejectOrder: (reason: string) => Promise<void>;
  onEditClick: () => void;
  onAcceptOrder: () => Promise<void>;
  onReceiveOrder: () => Promise<void>;
  onReportIncompleteDelivery: (
    reason: OrderIncompleteDeliveryReason,
    comment?: string,
  ) => Promise<void>;
  onResolveIncompleteDelivery: (incompleteDeliveryId: string) => Promise<void>;
  hasReportedIncompleteDelivery: boolean;
}

interface Counterparty {
  label: string;
  getName: (order: Order) => string;
}

interface OrderDetailsProps {
  renderNav?: (props: OrderDetailsNavProps) => ReactNode;
  counterparty?: Counterparty;
  renderOrderStatus?: (order: Order) => ReactNode;
  renderOrderNotice?: (props: OrderDetailsNavProps) => ReactNode;
}

const OrderDetails = ({
  renderNav,
  counterparty,
  renderOrderStatus,
  renderOrderNotice,
}: OrderDetailsProps) => {
  const [state, setState] = useState<State>({
    isEditing: false,
    hidePriceDataInPrint: false,
    confirmedQuantities: undefined,
    confirmedQuantitiesOrderId: undefined,
    deliveryWindow: undefined,
    reportedIncompleteDeliveryOrderId: undefined,
  });

  const { orderId } = useParams();
  const queryClient = useQueryClient();

  const ordersApi = new OrdersApi(new Configuration(useAuth));

  const { isLoading, order, invalidateQuery } = useGetOrderQuery(orderId);
  const orderItems = order?.items;
  const activeOrderId = order?.id;

  useEffect(() => {
    setState((prevState) => {
      if (activeOrderId === undefined || orderItems === undefined) {
        return prevState;
      }

      const confirmedQuantities = orderItems.reduce((quantities, item) => {
        quantities[item.id] = item.confirmedQuantity ?? item.quantity;
        return quantities;
      }, {} as OrderItemQuantities);

      if (
        prevState.confirmedQuantitiesOrderId === activeOrderId &&
        (prevState.isEditing ||
          areConfirmedQuantitiesEqual(prevState.confirmedQuantities, confirmedQuantities))
      ) {
        return prevState;
      }

      return {
        ...prevState,
        confirmedQuantities,
        confirmedQuantitiesOrderId: activeOrderId,
      };
    });
  }, [activeOrderId, orderItems]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!order) {
    return <div className="flex justify-center py-20">Something went wrong ...</div>;
  }

  const deliveryWindow = state.deliveryWindow
    ? DeliveryWindowImpl.fromObject(state.deliveryWindow)
    : order.deliveryWindow
      ? DeliveryWindowImpl.fromObject(order.deliveryWindow)
      : undefined;

  const handleEditClick = () => {
    setState((prevState) => ({
      ...prevState,
      isEditing: true,
    }));
  };

  const handleDiscardChangesClick = () => {
    setState((prevState) => ({
      ...prevState,
      isEditing: false,
      confirmedQuantities: undefined,
      confirmedQuantitiesOrderId: undefined,
    }));

    void invalidateQuery();
  };

  const handleConfirmedQuantityChange = (quantity: number, orderItemId: string) => {
    setState((prevState) => ({
      ...prevState,
      confirmedQuantities: {
        ...prevState.confirmedQuantities,
        [orderItemId]: quantity,
      },
      confirmedQuantitiesOrderId: order.id,
    }));
  };

  const { isEditing, confirmedQuantities } = state;

  const handleDeliveryWindowChange = (item: DeliveryWindowItem | null) => {
    setState((prevState) => ({
      ...prevState,
      deliveryWindow: item?.deliveryWindow,
    }));
  };

  const handleUpdateAndAcceptOrder = async (): Promise<void> => {
    if (!confirmedQuantities) {
      return;
    }

    try {
      await ordersApi.updateOrder(order.id, {
        confirmedDeliveryWindow: state.deliveryWindow,
        items: order.items.map((item) => {
          const confirmedQuantity =
            confirmedQuantities[item.id] ?? item.confirmedQuantity ?? item.quantity;
          const confirmedPrice = calculateConfirmedOrderItemPrice(item, confirmedQuantity);

          return {
            id: item.id,
            confirmedQuantity,
            confirmedLineItemPrice: confirmedPrice?.lineItemPrice,
            confirmedDiscountAmount: confirmedPrice?.discountAmount,
          };
        }),
      });

      await ordersApi.acceptOrder(order.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully updated and accepted order #${order.code}`}
          visible={t.visible}
        />
      ));

      void invalidateQuery();

      setState((prevState) => ({
        ...prevState,
        isEditing: false,
        confirmedQuantities: undefined,
        confirmedQuantitiesOrderId: undefined,
      }));
    } catch (error) {
      console.error('Error while accepting order', error);
    }
  };

  const handleAcceptOrder = async (): Promise<void> => {
    try {
      await ordersApi.acceptOrder(order.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully accepted order #${order.code}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
    } catch (error) {
      console.error('Error while accepting order', error);
    }
  };

  const handleDispatchOrder = async (): Promise<void> => {
    try {
      await ordersApi.dispatchOrder(order.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully dispatched order #${order.code}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
    } catch (error) {
      console.error('Error while dispatching order', error);
    }
  };

  const handleReceiveOrder = async (): Promise<void> => {
    try {
      await ordersApi.receiveOrder(order.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully received order #${order.code}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
    } catch (error) {
      console.error('Error while receiving order', error);
    }
  };

  const handleReportIncompleteDelivery = async (
    reason: OrderIncompleteDeliveryReason,
    comment?: string,
  ): Promise<void> => {
    try {
      await ordersApi.reportOrderIncompleteDelivery(order.id, { reason, comment });

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully reported incomplete delivery for order #${order.code}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
      setState((prevState) => ({
        ...prevState,
        reportedIncompleteDeliveryOrderId: order.id,
      }));
    } catch (error) {
      if (error instanceof OrderIncompleteDeliveryAlreadyReportedError) {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message={`Incomplete delivery has already been reported for order #${order.code}`}
            variant="warning"
            visible={t.visible}
          />
        ));
        setState((prevState) => ({
          ...prevState,
          reportedIncompleteDeliveryOrderId: order.id,
        }));
        return;
      }

      console.error('Error while reporting incomplete delivery', error);
      throw error;
    }
  };

  const handleCompleteOrder = async (): Promise<void> => {
    try {
      await ordersApi.completeOrder(order.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully completed order #${order.code}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
    } catch (error) {
      console.error('Error while completing order', error);
    }
  };

  const handleResolveIncompleteDelivery = async (incompleteDeliveryId: string): Promise<void> => {
    try {
      await ordersApi.resolveOrderIncompleteDelivery(order.id, incompleteDeliveryId);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully resolved incomplete delivery report for order #${order.code}`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'order-attention-items' || q.queryKey[0] === 'orders',
      });
    } catch (error) {
      console.error('Error while resolving incomplete delivery', error);
      throw error;
    }
  };

  const handleRejectOrder = async (reason: string): Promise<void> => {
    try {
      await ordersApi.rejectOrder(order.id, reason);
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Successfully rejected order #${order.id}`}
          visible={t.visible}
        />
      ));
      void invalidateQuery();
    } catch (error) {
      console.error('Error while rejecting order', error);
    }
  };

  const resetPrintState = () => {
    setState((prevState) => ({
      ...prevState,
      hidePriceDataInPrint: false,
    }));
  };

  const handlePrintOrder = ({ hidePriceData = false }: PrintOrderOptions = {}) => {
    if (!hidePriceData) {
      window.print();
      return;
    }

    setState((prevState) => ({
      ...prevState,
      hidePriceDataInPrint: true,
    }));

    const handleAfterPrint = () => resetPrintState();
    window.addEventListener('afterprint', handleAfterPrint, { once: true });

    window.setTimeout(() => {
      window.print();
      window.setTimeout(() => {
        window.removeEventListener('afterprint', handleAfterPrint);
        resetPrintState();
      }, 0);
    }, 0);
  };

  const hidePriceData = state.hidePriceDataInPrint;
  const hasReportedIncompleteDelivery = Boolean(order.unresolvedIncompleteDelivery);

  const navProps: OrderDetailsNavProps = {
    order,
    isEditing,
    onPrintOrder: handlePrintOrder,
    onDiscardChangesClick: handleDiscardChangesClick,
    onUpdateAndAcceptOrder: handleUpdateAndAcceptOrder,
    onDispatchOrder: handleDispatchOrder,
    onCompleteOrder: handleCompleteOrder,
    onRejectOrder: handleRejectOrder,
    onEditClick: handleEditClick,
    onAcceptOrder: handleAcceptOrder,
    onReceiveOrder: handleReceiveOrder,
    onReportIncompleteDelivery: handleReportIncompleteDelivery,
    onResolveIncompleteDelivery: handleResolveIncompleteDelivery,
    hasReportedIncompleteDelivery:
      hasReportedIncompleteDelivery || state.reportedIncompleteDeliveryOrderId === order.id,
  };

  const hasVat = (order: Order): boolean =>
    order.items.some((item) => item.vatRate != undefined && item.vatRate > 0);
  const showVat = hasVat(order);

  return (
    <>
      <PageHeader title={`Order #${order.code}`}>
        <div className="order-details-action-group">{renderNav?.(navProps)}</div>
      </PageHeader>

      <div
        className="flex flex-col sm:flex-row w-full"
        data-testid="order-details"
        data-order-code={order.code}
      >
        <div className="flex-1">
          <span className="block font-bold mb-1">Delivery Address:</span>

          <div className="block mb-4">
            <Address address={order.deliveryAddress} />
          </div>
        </div>

        <div className="flex-1">
          {counterparty && (
            <>
              <span className="block font-bold mb-1">{counterparty.label}</span>
              <p className="block mb-4">{counterparty.getName(order)}</p>
            </>
          )}

          {order.status !== OrderStatusModel.Rejected && (
            <>
              <span className="block font-bold mb-1">Expected Delivery Date:</span>
              <p className="block mb-4">
                {order.deliveryWindow && formatTimeWindow(order.deliveryWindow)}
              </p>

              <span className="block font-bold mb-1">Actual Delivery Date:</span>
              <p className="block mb-4">
                {order.actualDeliveryDate
                  ? formatTimeWindow(order.actualDeliveryDate)
                  : 'Not Tracked'}
              </p>
            </>
          )}

          {order.status === OrderStatusModel.Rejected && order.rejectReason != '' && (
            <>
              <span className="block font-bold mb-1">Rejection reason:</span>
              <p className="block mb-4">{order.rejectReason}</p>
            </>
          )}
        </div>

        <div className="flex-1">
          <span className="block font-bold mb-4">Order Status:</span>

          {renderOrderStatus?.(order)}
        </div>
      </div>

      {renderOrderNotice?.(navProps)}

      <h2 className="block font-bold mb-1 text-lg" data-testid="order-items-heading">
        Order Items
      </h2>

      <div className="space-y-6">
        {isEditing && (
          <div className="lg:w-96">
            <DeliveryWindowSelectBox
              value={deliveryWindow?.id || null}
              onSelect={(item) => handleDeliveryWindowChange(item)}
              supplierId={order.supplierId}
              storeId={order.storeId}
              defaultValue="first"
              hideIfEmpty
            />
          </div>
        )}
        <div>
          {isEditing ? (
            <OrderItemEditTable
              order={order}
              confirmedQuantities={confirmedQuantities}
              onChangeConfirmedQuantity={handleConfirmedQuantityChange}
              countryCode={order.deliveryAddress?.countryCode}
              showVat={showVat}
              hidePriceData={hidePriceData}
            />
          ) : (
            <OrderItemTable
              order={order}
              countryCode={order.deliveryAddress?.countryCode}
              showVat={showVat}
              hidePriceData={hidePriceData}
            />
          )}
        </div>
      </div>

      <div className="order-details-action-group mt-12 buttons">{renderNav?.(navProps)}</div>
    </>
  );
};

export default OrderDetails;
